/**
 * State and submission for a schema-driven lead form.
 *
 * Guarantees every form gets the same behaviour:
 *   - success is shown only after the API confirms (2xx); errors are real
 *   - one submission id per attempt-until-success, so retries never duplicate
 *   - form_start on first input, form_submit on submit, generate_lead on 2xx,
 *     all named by the schema id
 *   - honeypot + time-to-submit bot signals
 *   - optional schema.events { start, submit, invalid, success, error } for extra,
 *     page-specific analytics (landing-page funnels)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import analytics from "@/utils/analytics";
import { buildSubmission, flattenFields } from "./mapping";
import { newSubmissionId, postLead, stageFiles } from "./submit";
import { validateFields } from "./validation";

export const GENERIC_ERROR = "Sorry, we couldn't send that. Please try again, or call us on 0800 368 2989.";

function initialValues(schema, defaults) {
  const values = {};
  flattenFields(schema).forEach((field) => {
    if (field.default !== undefined) values[field.name] = field.default;
    else if (field.type === "chips" || field.type === "file") values[field.name] = [];
    else if (field.type === "checkbox") values[field.name] = false;
    else values[field.name] = "";
  });
  return { ...values, ...(defaults || {}) };
}

export default function useLeadForm(schema, { context = {}, defaults, onSuccess } = {}) {
  const [values, setValues] = useState(() => initialValues(schema, defaults));
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const [submitError, setSubmitError] = useState("");
  const [step, setStep] = useState(0);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [hp, setHp] = useState("");
  const [uploadProgress, setUploadProgress] = useState(null);

  const submissionIdRef = useRef(null);
  const acceptedSubmissionRef = useRef(null);
  const stagedRef = useRef({ id: null, keys: [] });
  const startedRef = useRef(false);
  const lockRef = useRef(false);
  const clockRef = useRef(null);

  // Set in an effect, not during render, so the react-snap snapshot and the
  // first client render stay identical.
  useEffect(() => {
    clockRef.current = Date.now();
  }, []);

  const steps = useMemo(
    () => (schema.steps ? schema.steps : [{ fields: schema.fields || [] }]),
    [schema]
  );
  const stepFields = useCallback(
    (index) => steps[index].fields.flatMap((entry) => (Array.isArray(entry) ? entry : [entry])),
    [steps]
  );

  // One start per form instance, at the first interaction while analytics
  // is allowed (Gary, 30 Sep 2026). formStart() returns false when consent
  // is unknown/denied — then the flag stays unconsumed so the first
  // permitted interaction still counts, and granting consent replays
  // nothing. The schema's own start events (tool_started, *_form_start)
  // follow the same rule. Popup suppression runs inside formStart() either way.
  const markStarted = useCallback(() => {
    if (startedRef.current) return;
    if (!analytics.formStart(schema.id)) return;
    startedRef.current = true;
    schema.events?.start?.();
  }, [schema]);

  const setValue = useCallback(
    (name, value) => {
      markStarted();
      setValues((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
      setSubmitError("");
    },
    [markStarted]
  );

  /** For forms revealed later (popups): time-to-submit counts from reveal. */
  const resetClock = useCallback(() => {
    clockRef.current = Date.now();
  }, []);

  const next = useCallback(() => {
    const stepErrors = validateFields(stepFields(step), values, context);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length) {
      analytics.formValidationError(schema.id, { product: context.product || "" });
      schema.events?.invalid?.(stepErrors);
      return false;
    }
    const nextStep = Math.min(step + 1, steps.length - 1);
    if (nextStep !== step) {
      setStep(nextStep);
      analytics.formStep(schema.id, "next", `step_${nextStep + 1}`, { product: context.product || "" });
    }
    return true;
  }, [schema, step, stepFields, steps.length, values, context]);

  const back = useCallback(() => {
    const previousStep = Math.max(step - 1, 0);
    if (previousStep === step) return;
    setStep(previousStep);
    analytics.formStep(schema.id, "back", `step_${previousStep + 1}`, { product: context.product || "" });
  }, [schema, step, context]);

  const submit = useCallback(
    async (event) => {
      if (event && event.preventDefault) event.preventDefault();
      if (lockRef.current) return;
      lockRef.current = true;
      analytics.formSubmit(schema.id, { product: context.product || "" });
      schema.events?.submit?.();

      const allFields = steps.flatMap((_, i) => stepFields(i));
      const allErrors = validateFields(allFields, values, context);
      setErrors(allErrors);
      if (Object.keys(allErrors).length) {
        analytics.formValidationError(schema.id, { product: context.product || "" });
        schema.events?.invalid?.(allErrors);
        const firstBad = steps.findIndex((_, i) => stepFields(i).some((f) => allErrors[f.name]));
        if (firstBad >= 0) setStep(firstBad);
        lockRef.current = false;
        return;
      }

      if (!submissionIdRef.current) submissionIdRef.current = newSubmissionId();
      const submissionId = submissionIdRef.current;
      setStatus("submitting");
      setSubmitError("");
      try {
        const files = allFields.filter((f) => f.type === "file").flatMap((f) => values[f.name] || []);
        let fileKeys = stagedRef.current.id === submissionId ? stagedRef.current.keys : [];
        if (files.length && !fileKeys.length) {
          setUploadProgress(0);
          try {
            const stagedKeys = await stageFiles(submissionId, files, setUploadProgress);
            if (!Array.isArray(stagedKeys)) throw new Error("Upload staging did not return file keys");
            fileKeys = stagedKeys;
          } catch (uploadError) {
            if (schema.id === "statement_review") analytics.statementTransferError();
            throw uploadError;
          }
          stagedRef.current = { id: submissionId, keys: fileKeys };
          if (schema.id === "statement_review") analytics.statementTransferSuccess();
        }
        const body = buildSubmission({
          schema,
          values,
          context,
          submissionId,
          marketingConsent,
          hp,
          fileKeys,
          elapsedMs: clockRef.current ? Math.round(Date.now() - clockRef.current) : null,
        });
        await postLead(body);
      } catch (err) {
        setStatus("idle");
        setUploadProgress(null);
        setSubmitError(err && err.status === 422 && err.message ? err.message : schema.errorMessage || GENERIC_ERROR);
        analytics.formRequestError(schema.id, { product: context.product || "" });
        schema.events?.error?.(err);
        lockRef.current = false;
        return;
      }
      setStatus("success");
      setUploadProgress(null);
      // A backend 2xx is the historical generate_lead definition. The ref
      // makes this explicit per form instance/submission, rather than using a
      // cross-form time window that can suppress a real second enquiry.
      if (acceptedSubmissionRef.current !== submissionId) {
        acceptedSubmissionRef.current = submissionId;
        analytics.generateLead(schema.id, { product: context.product || "", attemptId: submissionId });
      }
      schema.events?.success?.();
      if (onSuccess) onSuccess({ id: submissionId, values });
    },
    [schema, steps, stepFields, values, context, marketingConsent, hp, onSuccess]
  );

  // "Send another message" after a success: a new form instance. It gets a
  // fresh submission id and its own form_start on its first permitted
  // interaction (a remount is likewise a new instance).
  const reset = useCallback(() => {
    startedRef.current = false;
    submissionIdRef.current = null;
    acceptedSubmissionRef.current = null;
    stagedRef.current = { id: null, keys: [] };
    lockRef.current = false;
    setValues(initialValues(schema, defaults));
    setErrors({});
    setStep(0);
    setStatus("idle");
    setSubmitError("");
  }, [schema, defaults]);

  return {
    values, setValue, errors, status, submitError, step, steps, next, back, submit, reset,
    marketingConsent, setMarketingConsent, hp, setHp, resetClock, uploadProgress,
  };
}
